import React, { useState, useEffect } from 'react';
import { CreateContact } from './CreateContact';
import { Field, normalizeValueToIndex } from './Section';
import Alert from './Alert';

export const LeadIncident = ({ customFields, leadData, fetchCustomFields, fieldData, fetchLead, contacts, fetchContacts }) => {
    const [formData, setFormData] = useState({});
    const [newData, setNewData] = useState({});
    const [createContact, setCreateContact] = useState(false);
    const [newContact, setNewContact] = useState({});
    const [contactTypes, setContactTypes] = useState([]);
    const [newContactFieldId, setNewContactFieldId] = useState(null);
    const [caseTypes, setCaseTypes] = useState([]);
    const [message, setMessage] = useState(null);

    const fetchCaseTypes = async () => {
        try {
            const response = await fetch("https://api.casedb.co/case-types.php");
            const data = await response.json();
            setCaseTypes(data.case_types);
        } catch (err) {
            console.error(err);
        }
    };

    console.log('Lead Data:', leadData, fieldData, customFields);

    const updateFields = async () => {
        await fetch('https://api.casedb.co/custom_fields.php', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(newData)
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    fetchCustomFields();
                    if (fetchLead) fetchLead();
                    setNewData({});
                    setMessage({ text: "Lead data updated successfully.", type: "success" });
                } else {
                    setMessage({ text: "Failed to update fields.", type: "error" });
                }
            })
            .catch(error => setMessage({ text: "Error updating fields.", type: "error" }));

        if (formData.case_type_id) {
            const response = await fetch('https://api.casedb.co/leads.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    lead_id: leadData.id,
                    field: 'case_type_id',
                    value: formData.case_type_id
                }),
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                fetchLead();
                formData.case_type_id = null; // Reset after update
                setMessage({ text: "Lead case type updated successfully.", type: "success" });
            } else {
                setMessage({ text: "Failed to update lead data.", type: "error" });
            }
        }
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
        setNewData((prev) => ({
            ...prev,
            lead_id: leadData.id,
            field_values: {
            ...(prev.field_values || {}),
            [fieldId]: (typeof value === 'object')
                ? JSON.stringify(value)
                : value
            }
        }));
    };

    const getFieldValue = (field) => {
        if (field.add_item === 1) {
            return formData[field.id];
        } else {
            const val = formData[field.id];
            if (val !== undefined && val !== null) return val;
    
            const stored = fieldData?.[0]?.[field.id];
            if (stored !== undefined) return stored;
    
            if (field.field_id === 17) {
                return;
            }
    
            if (field.field_id === 12) return 2;
    
            return '';
        }
    };

    const handleNewContactCreated = (contact) => {
        if (newContactFieldId !== null && contact?.id) {
            handleFieldChange(newContactFieldId, contact.id);
        }
        setCreateContact(false);
        setNewContactFieldId(null);
    };

    const shouldDisplay = (field) => {
        if (field.add_item === 1) return false;
    
        if (Number(field.case_type_id) !== Number(leadData.case_type_id) && Number(field.case_type_id) !== 0) return false;
    
        // Handle database expressions like "leads.marketing_source = 33"
        if (field.is_answered && field.is_answered.includes('=') && leadData) {
            try {
                // Parse expressions like "leads.marketing_source = 33"
                const expression = field.is_answered.trim();
                const [leftSide, operator, rightSide] = expression.split(/\s*([=!<>]+)\s*/);
                
                if (leftSide && operator && rightSide) {
                    // Extract table.field format
                    const [table, fieldName] = leftSide.split('.');
                    let actualValue;
                    
                    if (table === 'leads' && leadData) {
                        actualValue = leadData[fieldName];
                    }
                    
                    const expectedValue = rightSide.replace(/['"]/g, ''); // Remove quotes
                    
                    switch (operator) {
                        case '=':
                        case '==':
                            return String(actualValue) === String(expectedValue);
                        case '!=':
                        case '<>':
                            return String(actualValue) !== String(expectedValue);
                        case '>':
                            return Number(actualValue) > Number(expectedValue);
                        case '<':
                            return Number(actualValue) < Number(expectedValue);
                        case '>=':
                            return Number(actualValue) >= Number(expectedValue);
                        case '<=':
                            return Number(actualValue) <= Number(expectedValue);
                        default:
                            return true;
                    }
                }
            } catch (error) {
                console.error('Error parsing is_answered expression:', field.is_answered, error);
                return true;
            }
        }
    
        // If no display_when condition, show the field
        if (!field.display_when) return true;

        const controllingField = customFields.find(f => f.id === field.display_when);
        if (!controllingField) return true;
    
        let controllingValue = getFieldValue(controllingField);

        if (field.is_answered === '[!]') {
            return controllingValue === null || controllingValue === undefined || controllingValue === "";
        }
    
        if (field.is_answered === null || field.is_answered === "") {
            return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
        }
    
        controllingValue = normalizeValueToIndex(controllingField, controllingValue);
    
        let parsedAnswers = [];
        try {
            const parsed = JSON.parse(field.is_answered);
            if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
            else parsedAnswers = [String(parsed)];
        } catch {
            parsedAnswers = String(field.is_answered).split(',').map(v => v.trim());
        }
        
        const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
        const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

        if (isNegated) {
            return !cleanAnswers.includes(String(controllingValue));
        } else {
            return cleanAnswers.includes(String(controllingValue));
        }
    };

    const dependencyMap = {};
    customFields.forEach(field => {
        if (field.display_when) {
            if (!dependencyMap[field.display_when]) dependencyMap[field.display_when] = [];
            dependencyMap[field.display_when].push(field);
        }
    });

    const rendered = new Set();
    const output = [];
    let currentSubSection = [];
    let currentInstructionGroup = [];
    let currentSubGroup = [];
    let currentBooleanFieldId = null;

    const flushSubSection = () => {
        if (currentSubSection.length > 0) {
            if (currentSubSection[0].props?.field?.field_id === 13) {
                output.push(
                    <React.Fragment key={`frag-${output.length}`}>
                        {currentSubSection}
                    </React.Fragment>
                );
            } else {
                output.push(
                    <div className="sub-section" key={`sub-${output.length}`}>
                        {currentSubSection}
                    </div>
                );
            }
            currentSubSection = [];
        }
    };

    const flushInstructionGroup = () => {
        if (currentInstructionGroup.length > 0) {
            output.push(
                <div className="instruction-group" key={`instruction-${output.length}`}>
                    {currentInstructionGroup}
                </div>
            );
            currentInstructionGroup = [];
        }
    };

    const flushSubGroup = () => {
        if (currentSubGroup.length > 0 && currentBooleanFieldId !== null) {
            const validSubGroupContent = currentSubGroup.filter(content => content !== null && content !== undefined);
            
            if (validSubGroupContent.length > 0) {
                const subGroupContent = (
                    <div className={`sub-boolean field-${currentBooleanFieldId}`} key={`sub-boolean-${currentBooleanFieldId}`}>
                        {validSubGroupContent}
                    </div>
                );
                currentSubSection.push(subGroupContent);
            }
        }
        
        currentSubGroup = [];
        currentBooleanFieldId = null;
    };

    customFields
        .filter(f => f.hidden !== 2 && f.obsolete !== 2)
        .sort((a, b) => a.order_id - b.order_id)
        .forEach((field, index) => {
            if (rendered.has(field.id)) return;
            if (!shouldDisplay(field)) return;

            rendered.add(field.id);

            const children = dependencyMap[field.id] || [];
            const visibleChildren = children.filter(shouldDisplay);
            
            const fieldEl = (
                <Field
                    key={`${field.id}-${index}`}
                    field={field}
                    value={getFieldValue(field)}
                    handleFieldChange={handleFieldChange}
                    formData={formData}
                    fields={customFields}
                    lead_id={leadData.id}
                    sectionName={'Case Details'}
                    setNewContactFieldId={setNewContactFieldId}
                    setCreateContact={setCreateContact}
                />
            );
            
            const childrenEls = visibleChildren.map((child, childIndex) => {
                if (!rendered.has(child.id)) {
                    rendered.add(child.id);
                }

                return (
                    <Field
                        key={`child-${child.id}-${childIndex}`}
                        field={child}
                        value={getFieldValue(child)}
                        handleFieldChange={handleFieldChange}
                        formData={formData}
                        fields={customFields}
                        lead_id={leadData.id}
                        sectionName={'Case Details'}
                        setNewContactFieldId={setNewContactFieldId}
                        setCreateContact={setCreateContact}
                    />
                );
            });
            
            if (visibleChildren.length > 0) {
                children.forEach(child => {
                    if (shouldDisplay(child)) {
                        rendered.add(child.id);
                    }
                });
            }

            if (field.field_id === 13) {
                flushSubGroup();
                flushInstructionGroup();
                flushSubSection();
                currentSubSection.push(fieldEl, ...childrenEls);
                flushSubSection();
            } else if (field.field_id === 14) {
                flushSubGroup();
                flushSubSection();
                currentInstructionGroup.push(fieldEl, ...childrenEls);
            } else if (field.field_id === 12 && field.display_when == null) {
                flushSubGroup();
                flushInstructionGroup();
                currentBooleanFieldId = field.id;
                currentSubGroup.push(fieldEl, ...childrenEls);
            } else {
                if (currentBooleanFieldId && field.display_when === currentBooleanFieldId) {
                    currentSubGroup.push(fieldEl, ...childrenEls);
                } else {
                    flushSubGroup();
                    flushInstructionGroup();
                    currentSubSection.push(fieldEl, ...childrenEls);
                }
            }
        });

    flushSubGroup();
    flushSubSection();
    flushInstructionGroup();

    useEffect(() => {
        fetchCaseTypes();
    }, []);

    return (
        <div className="lead-incident case-section">
            <div className='toc-header'>
                <h3>Incident Details</h3>
                <button className='action' onClick={() => updateFields()}>Save</button>
            </div>
            
            {customFields.length === 0 ? (
                <p>No incident details available for this lead.</p>
            ) : (
                <div className="sub-section">
                    <div className='form-group nm'>
                        <label className='subtext'>Case Type</label>
                        <select
                            value={formData.case_type_id ?? leadData.case_type_id}
                            onChange={(e) => handleFieldChange('case_type_id', e.target.value)}
                            className='default-select'
                        >
                            {caseTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {output}
                </div>
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    fetchContacts={fetchContacts} 
                    contactTypes={contactTypes} 
                    onContactCreated={handleNewContactCreated}
                />
            )}
            {message && <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />}
        </div>
    );
};
